import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useForm, SubmitHandler } from "react-hook-form";
import { logIn } from "../../redux/actions/auth.actions";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Link as RouterLink } from "react-router-dom";
import { useNavigate, useLocation } from "react-router";
import FlashMessage from "../../components/Flashmessage";
import TokenService from "../../utility";
import { Helmet } from "react-helmet";

function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="https://rosterme.io/">
        Rosterme.io
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const theme = createTheme();

type FormInputs = {
  email: string;
  password: string;
};

export default function SignIn(props: any) {
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.auth);
  const ui = useSelector((state: any) => state.UI);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: "onChange",
  });
  const navigate = useNavigate();
  const location = useLocation();

  const [, setApierrors] = useState();
  const [, setLoading] = useState(false);
  const [, setOpen] = useState(true);

  useEffect(() => {
    if (ui.errors) {
      setApierrors(ui.errors);
      setOpen(true);
    }
    setLoading(ui.loading);
  }, [ui, auth]);

  if (auth.authenticated) {
    return <Navigate to="/" />;
  } else {
    TokenService.clear();
  }

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    await dispatch<any>(logIn(data, navigate, location));
  };

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Rosterme SignIn</title>
      </Helmet>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          <FlashMessage />
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              autoComplete="email"
              label="Email"
              error={!!errors.email}
              {...register("email", { required: true })}
              helperText={
                errors.email &&
                errors.email.type === "required" &&
                "Email is required"
              }
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              error={!!errors.password}
              {...register("password", { required: true })}
              helperText={
                errors.password &&
                errors.password.type === "required" &&
                "Password is required"
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={ui.loading}
            >
              {ui.loading ? "Signing In" : "Sign In"}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link
                  component={RouterLink}
                  to="/forgetpassword"
                  variant="body2"
                >
                  Forgot password?
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Copyright sx={{ mt: 8, mb: 4 }} />
      </Container>
    </ThemeProvider>
  );
}
